import React, { useState, useEffect, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useNavigate } from 'react-router-dom';
import { Row, Col, CardBody, Card } from "reactstrap";
import LoadingOverlay from "react-loading-overlay-ts";
import DNALoader from "../Loaders/DnaLoader";
import DataTable from 'react-data-table-component';
import axios from 'axios';
import { FaEye, FaTrash, FaEdit } from 'react-icons/fa';
import Swal from "sweetalert2";
import { Tooltip } from "@mui/material";
import AddDocumentModal from './AddDocumentModal';
import EditDocumentModal from './EditDocumentModal';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import PermissionAccess from '../../PermissionAccess/Access.js';
import Alerts from "../Alerts/alerts";
import AuthService from "../../pages/Authentication/auth.service";

const API_BASE_URL = process.env.REACT_APP_APIURL;

const MerchantDocsPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [selectedRows, setSelectedRows] = useState([]);
  const [pageLimit, setPageLimit] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
  const { authData, accessPages, super_admin, current_module } = PermissionAccess();

  // Helper function to refresh current page data
  const refreshCurrentPage = () => {
    fetchDocuments(currentPage, pageLimit, filterText, filterStatus, filterType);
  };

  const [showModalAddDocument, setShowModalAddDocument] = useState(false);
  const handleToggleModalAddDocument = () => {
    setShowModalAddDocument(!showModalAddDocument);
  }

  const [showModalEditDocument, setShowModalEditDocument] = useState(false);
  const handleToggleModalEditDocument = (id) => {
    setShowModalEditDocument(!showModalEditDocument);
    setSelectedId(id);
  };

  const [showModalDeleteDocument, setShowModalDeleteDocument] = useState(false);
  const handleToggleModalDeleteDocument = (id) => {
    setShowModalDeleteDocument(!showModalDeleteDocument);
    setSelectedId(id);
  };

  useEffect(() => {
    i18n.changeLanguage(localStorage.getItem('language'));
    fetchDocuments(1, pageLimit);
  }, [pageLimit]);

  function fetchDocuments(page_index, page_limit, text, status, type) {
    setLoading(true);
    var offset = (page_index - 1) * page_limit;
    if (offset < 1) {
      offset = 0;
    }

    // Convert status string for backend
    let statusParam = '';
    if (status === 'Active') {
      statusParam = 'Active';
    } else if (status === 'InActive') {
      statusParam = 'InActive';
    }

    console.log('Fetching documents with params:', { page_index, page_limit, text, status, statusParam, type });
    
    const requestData = {
      functionName: 'GetMerchantDocs',
      postData: {
        offset: offset,
        limit: page_limit,
        sort: { datetime_added: -1 },
        text: text || '',
        status: statusParam,
        type: type || '',
        noActiveMatch: true
      }
    };

    const headers = {
      'Content-Type': 'application/json',
      'token': localStorage.getItem('session-id')
    };

    axios.post(`${API_BASE_URL}/GetMerchantDocs`, requestData, { headers }).then(
      (response) => {
        setLoading(false);
        console.log("Get documents response:-", response);
        if (response.data.message === "Unauthorized") {
          AuthService.logout();
          navigate("/login");
          window.location.reload();
        } else {
          console.log(response.data);
          const newTotalRows = response.data.count?.count || 0;
          const newDocuments = response.data.data || [];
          
          // Check if current page is still valid after data changes
          const maxPage = Math.ceil(newTotalRows / page_limit) || 1;
          let adjustedPage = page_index;
          
          // If current page is beyond the available pages, go to the last available page
          if (page_index > maxPage && newTotalRows > 0) {
            adjustedPage = maxPage;
            setCurrentPage(maxPage);
            // Re-fetch with the adjusted page
            fetchDocuments(maxPage, page_limit, text, status, type);
            return;
          }
          
          // If no data and we're not on page 1, go to page 1
          if (newTotalRows === 0 && page_index > 1) {
            adjustedPage = 1;
            setCurrentPage(1);
            // Re-fetch with page 1
            fetchDocuments(1, page_limit, text, status, type);
            return;
          }
          
          setDocuments(newDocuments);
          setTotalRows(newTotalRows);
        }
      },
      (error) => {
        setLoading(false);
        const errContent =
          (error.response && error.response.data) ||
          error.message ||
          error.toString();
        if (errContent) {
          // Check if it's an object with a message property
          if (typeof errContent === 'object' && errContent.message) {
            Alerts.swalErrorAlert(errContent.message);
          } else if (typeof errContent === 'string') {
            Alerts.swalErrorAlert(errContent);
          } else {
            Alerts.swalErrorAlert(t("Server Error, Please try again later."));
          }
      } else {
          Alerts.swalErrorAlert(t("Server Error, Please try again later."));
        }
      }
    );
  }

  function confirmDelete(_id) {
    let msg = t("Once deleted, you will not be able to recover this document!")
    Alerts.swalConfirmDeleteAlert(msg).then(res => {
      console.log("Resp:-", res)
      if (res === true) {
        sendDelete(_id)
      }
    })
  };

  function sendDelete(id) {
    setLoading(true);
    const requestData = {
      functionName: 'DeleteMerchantDoc',
      postData: {
        doc_id: id
      }
    };

    const headers = {
      'Content-Type': 'application/json',
      'token': localStorage.getItem('session-id')
    };

    axios.post(`${API_BASE_URL}/DeleteMerchantDoc`, requestData, { headers }).then((response) => {
      console.log("Delete Document response:-", response)
      setLoading(false);
      refreshCurrentPage();
      if (response.data.status) {
        Alerts.swalSuccessAlert(t("Document Deleted Successfully."))
        console.log(response.data.message)
      }
      else {
        setLoading(false);
        if (response.data.message !== '') {
          if (response.data.message === "Unauthorized") {
            AuthService.logout();
            navigate("/login");
            window.location.reload();
          }
          else {
            Alerts.swalErrorAlert(response.data.message);
          }
        }
        else {
          Alerts.swalErrorAlert(t("Error: Can't delete the Document."))
        }
      }
    },
      (error) => {
        setLoading(false);
        const errContent =
          (error.response && error.response.data) || error.message || error.toString();
        if (typeof errContent === 'object' && errContent.message) {
          Alerts.swalErrorAlert(errContent.message);
        } else if (typeof errContent === 'string') {
          Alerts.swalErrorAlert(errContent);
        } else {
          Alerts.swalErrorAlert(t("Delete operation failed"));
        }
      })
  };

  const handleCheckboxChange = (e, row) => {
    const { checked } = e.target;
    if (checked) {
      setSelectedRows((prevSelected) => [...prevSelected, row._id]);
    } else {
      setSelectedRows((prevSelected) => prevSelected.filter(id => id !== row._id));
    }
  };

  const handleSelectAllChange = (e) => {
    const { checked } = e.target;
    if (checked) {
      setSelectedRows(documents.map((document) => document._id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleDeleteSelected = () => {
    if (selectedRows.length > 0) {
      let msg = t(selectedRows.length + " records selected. Once deleted, you will not be able to recover this!");
      Alerts.swalConfirmDeleteAlert(msg).then(res => {
        console.log("Resp:-", res)
        if (res === true) {
          sendBulkDelete(selectedRows)
        }
      })
    } else {
      Alerts.swalErrorAlert('No records selected');
    }
  };

  function sendBulkDelete(selectedRows) {
    setLoading(true);
    const requestData = {
      functionName: 'DeleteBulkMerchantDocs',
      postData: {
        doc_ids: selectedRows
      }
    };

    const headers = {
      'Content-Type': 'application/json',
      'token': localStorage.getItem('session-id')
    };

    axios.post(`${API_BASE_URL}/DeleteBulkMerchantDocs`, requestData, { headers }).then((response) => {
      console.log("Delete Bulk Documents response:-", response)
      setLoading(false);
      setSelectedRows([]);
      refreshCurrentPage();
      
      if (response.data.status) {
        Alerts.swalSuccessAlert(t("Documents Deleted Successfully."))
      } else {
        Alerts.swalErrorAlert(t("Some documents could not be deleted."))
      }
    },
      (error) => {
        setLoading(false);
        const errContent =
          (error.response && error.response.data) || error.message || error.toString();
        if (typeof errContent === 'object' && errContent.message) {
          Alerts.swalErrorAlert(errContent.message);
        } else if (typeof errContent === 'string') {
          Alerts.swalErrorAlert(errContent);
        } else {
          Alerts.swalErrorAlert(t("Delete operation failed"));
        }
      })
  };

  const handleSearch = (searchText, status, type) => {
    setFilterText(searchText);
    setFilterStatus(status);
    setFilterType(type);
    setCurrentPage(1);
    fetchDocuments(1, pageLimit, searchText, status, type);
  };

  const handleClear = () => {
    setFilterText('');
    setFilterStatus('');
    setFilterType('');
    setCurrentPage(1);
    fetchDocuments(1, pageLimit);
  };

  function handlePageChange(page_index) {
    console.log("Page index:-", page_index)
    setCurrentPage(page_index)
    fetchDocuments(page_index, pageLimit, filterText, filterStatus, filterType);
  }

  async function handlePerRowsChange(newPageLimit, page_index) {
    setPageLimit(newPageLimit);
  }

  const mycolumns = [
    {
      name: (
        <div>
            <input
            className="form-check-input"
            type="checkbox"
            onChange={handleSelectAllChange}
          />
          </div>
      ),
      width: '10%',
      selector: (row) => {
  return (
          <div>
            <input
              className="form-check-input"
              type="checkbox"
              checked={selectedRows.includes(row._id)}
              onChange={(e) => handleCheckboxChange(e, row)}
            />
            </div>
        );
      },
    },
    {
      name: t('Title'),
      selector: "title",
      width: "25%",
      sortable: true,
    },
    {
      name: t('Icon'),
      selector: "icon",
      width: "15%",
      sortable: true,
    },
    {
      name: t('Type'),
      width: '15%',
      selector: (row) => {
        return (
          <div>
            <span className={`badge ${row.type === 'pharmacy' ? 'bg-primary' : row.type === 'commerce' ? 'bg-info' : 'bg-secondary'}`}>
              {row.type ? row.type.charAt(0).toUpperCase() + row.type.slice(1) : 'N/A'}
            </span>
          </div>
        );
      },
      sortable: true,
    },
    {
      name: t('Status'),
      width: '15%',
      selector: (row) => {
        return (
          <div>
            {row.status === 'active' || row.status === 'Active' ? (
              <button
                type="button"
                style={{ borderColor: '#3ac47d', backgroundColor: '#1BD35A' }}
                className="btn btn-sm btn-success btn-hover-shine me-3"
              >
                Active
              </button>
            ) : (
              <button
                type="button"
                style={{ borderColor: '#FF0000', backgroundColor: 'red' }}
                className="btn btn-sm btn-danger btn-hover-shine me-3"
              >
                InActive
              </button>
            )}
          </div>
        );
      },
      sortable: true,
    },
    {
      name: t('Actions'),
      width: "20%",
      selector: (row) => {
        return (
          <div>
            {!super_admin ? current_module?.EditData === 1 &&
              <Tooltip placement="top" title={`${t("Update")} ${t("Document")}`} arrow onClick={() => handleToggleModalEditDocument(row._id)} style={{ cursor: "pointer" }}>
                <i className="pe-7s-pen btn-icon-wrapper text-primary fs-5 p-1"> </i>
              </Tooltip>
              :
              <Tooltip placement="top" title={`${t("Update")} ${t("Document")}`} arrow onClick={() => handleToggleModalEditDocument(row._id)} style={{ cursor: "pointer" }}>
                <i className="pe-7s-pen btn-icon-wrapper text-primary fs-5 p-1"> </i>
              </Tooltip>
            }
            {!super_admin ? current_module?.DeleteData === 1 &&
              <Tooltip placement="top" title={`${t("Delete")} ${t("Document")}`} arrow onClick={() => confirmDelete(row._id)} style={{ cursor: "pointer" }}>
                <i className="pe-7s-trash text-danger btn-icon-wrapper fs-5"> </i>
              </Tooltip>
              :
              <Tooltip placement="top" title={`${t("Delete")} ${t("Document")}`} arrow onClick={() => confirmDelete(row._id)} style={{ cursor: "pointer" }}>
                <i className="pe-7s-trash text-danger btn-icon-wrapper fs-5"> </i>
              </Tooltip>
            }
        </div>
        );
      }
    },
  ];

  const filteredItems = documents;

  const subHeaderComponentMemo = React.useMemo(() => {
    return (
      <div className="d-flex flex-wrap gap-2 align-items-end">
        <div className="me-2">
          <input
            type="text"
            className="form-control"
            placeholder={t("Search Text")}
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSearch(filterText, filterStatus, filterType);
              }
            }}
          />
        </div>
        <div className="me-2">
          <select
            className="form-select"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="">{t("Select Status")}</option>
            <option value="Active">{t("Active")}</option>
            <option value="InActive">{t("InActive")}</option>
          </select>
        </div>
        <div className="me-2">
          <select
            className="form-select"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="">{t("Select Type")}</option>
            <option value="pharmacy">{t("Pharmacy")}</option>
            <option value="commerce">{t("Commerce")}</option>
            <option value="both">{t("Both")}</option>
          </select>
        </div>
      </div>
    );
  }, [filterText, filterStatus, filterType]);

  return (
    <Fragment>
      <div className="app-main__outer">
        <div className="app-main__inner">
          <div className="bg-light-gray bg-animation">
            <div className="d-flex justify-content-center align-items-center">
              <Col md="12" className="mx-auto app-login-box">
                <div className="app-page-title">
                  <div className="page-title-wrapper">
                    <div className="page-title-heading fw-bold">
                      <div className="page-title-icon">
                        <i className="pe-7s-note icon-gradient bg-tempting-azure" />
                      </div>
                      <div>
                        {`${t("Merchant")} ${t("Documents")}`}
                        <div className="page-title-subheading">
                          {`${t("Add, Update & Delete of")} ${t("Documents")}`}
    </div>
  </div>
</div>
                    <div className="page-title-actions">
                      <AddDocumentModal 
                        showModalAddDocument={showModalAddDocument} 
                        handleToggleModalAddDocument={handleToggleModalAddDocument} 
                        onSuccess={refreshCurrentPage} 
                      />
                      <EditDocumentModal
                        onSuccess={refreshCurrentPage}
                        showModalEditDocument={showModalEditDocument}
                        handleToggleModalEditDocument={() => handleToggleModalEditDocument(null)}
                        id={selectedId}
                      />
                    </div>
                      </div>
        </div>

                <Row>
                  <LoadingOverlay
                    tag="div"
                    active={loading}
                    styles={{ overlay: (base) => ({ ...base }) }}
                    spinner={<DNALoader />}
                  >
                    <Col md="12">
                      <Card className="main-card mb-3">
                        <CardBody>
                          <Col md="6" style={{ float: "left", position: "absolute", zIndex: "1" }}>
                            {subHeaderComponentMemo}
                          </Col>
                          <DataTable
                            data={filteredItems}
                            columns={mycolumns}
                            pagination
                            fixedHeader
                            fixedHeaderScrollHeight="400px"
                            subHeader
                            subHeaderComponent={
                              <div className="d-flex flex-wrap gap-2 align-items-center">
                                <div className="d-flex gap-2">
                                  <button
                                    type="button"
                                    className="btn btn-custom-color btn-hover-shine btn"
                                    onClick={() => handleSearch(filterText, filterStatus, filterType)}
                                  >
                                    {t("Search")}
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-custom-color btn-hover-shine btn"
                                    onClick={handleClear}
                                  >
                                    {t("Reset")}
                                  </button>
                                </div>
                                
                                {!super_admin ? current_module?.BulkDelete === 1 &&
                                  <button type="button"
                                    className="btn btn-outline-danger btn-hover-shine btn"
                                    onClick={handleDeleteSelected}
                                  >
                                    {t('Delete Selected')}
              </button>
                                  :
                                  <button type="button"
                                    className="btn btn-outline-danger btn-hover-shine btn"
                                    onClick={handleDeleteSelected}
                                  >
                                    {t('Delete Selected')}
              </button>
                                }
                                
                                {!super_admin ? current_module?.AddData === 1 &&
                                  <button type="button" className="btn btn-custom-color btn-hover-shine btn" onClick={handleToggleModalAddDocument}>
                                    {t('Add New')}
              </button>
                                  :
                                  <button type="button" className="btn btn-custom-color btn-hover-shine btn" onClick={handleToggleModalAddDocument}>
                                    {t('Add New')}
              </button>
                                }
                              </div>
                            }
                            paginationServer
                            paginationTotalRows={totalRows}
                            onChangePage={handlePageChange}
                            onChangeRowsPerPage={handlePerRowsChange}
                            paginationResetDefaultPage={resetPaginationToggle}
                          />
                        </CardBody>
                      </Card>
                    </Col>
                  </LoadingOverlay>
                </Row>
              </Col>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default MerchantDocsPage;